import {isEmpty, xor} from 'lodash';
import moment from 'moment-timezone';
import {useCallback, useMemo, useState, useEffect, useContext} from 'react';
import {useTranslation} from 'react-i18next';
import {WritingOptions} from 'xlsx';

import {sessionHistoryApi} from 'api';
import {GlobalLoader, Button, notifier} from 'components';
import {APP_NAME} from 'constants/app';
import {UserContext} from 'context';
import {TaskStatus} from 'enums';
import {usePrevious} from 'hooks';
import {SessionActivityName} from 'interfaces';
import {TRANSLATION_KEYS} from 'locale/translation-keys';
import {DataRequest} from 'services';
import {PaginatedData, PaginationOptions} from 'types';
import {settingsUtils} from 'utils';
import {lazyModules} from 'utils/lazy-modules';
import {transformUtils} from 'utils/transform';

import {REQUEST_SESSION_HISTORY_DATE_FORMAT} from '../../constants';
import {ActivityFilters} from '../../types';

import {ExportReportModal} from './ExportReportModal';
import {ModalResult, ReportColumn} from './interfaces';

const T_KEYS = TRANSLATION_KEYS.USER.ACTIVITY.SESSION_HISTORY.DOWNLOAD_REPORT;
const ACTIVITY_T_KEYS = TRANSLATION_KEYS.USER.ACTIVITY;
const ACTIVITY_NAMES = TRANSLATION_KEYS.FEATURES.SESSION.ACTIVITY;

type Props = {
    pagination: PaginationOptions,
    filters: ActivityFilters;
    isLoading: boolean;
    disabled?: boolean;
    className?: string;
};

export const DownloadReport: React.FC<Props> = ({pagination, filters, isLoading, disabled, className}: Props) => {
    const {settings} = useContext(UserContext);
    const {t} = useTranslation();
    const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);

    const [reportLoading, setReportLoading] = useState(false);
    const prevReportLoading = usePrevious(reportLoading);
    const [reportRequestProgress, setReportRequestProgress] = useState(0);
    const [reportItems, setReportItems] = useState([]);
    const [downloadModalResult, setDownloadModalResult] = useState<ModalResult>(null);
    const prevDownloadModalResult = usePrevious(downloadModalResult);

    const {dateFormat} = useMemo(() => settings.common, [settings.common]);
    const fileDateFormat = useMemo(() => transformUtils.getFileDateFormat(dateFormat), [dateFormat]);

    const canSeeFilePipeline = useMemo(() => settingsUtils.canSeeFilePipeline(settings), [settings]);
    const externalFilesCount = useMemo(() => settings.afterSession.externalFiles.count, [settings]);

    const reportColumns: ReportColumn[] = useMemo(() => [
        ReportColumn.StartDate,
        ReportColumn.EndDate,
        ReportColumn.Duration,
        ReportColumn.AgentName,
        ReportColumn.ClientId,
        ReportColumn.ClientName,
        ReportColumn.ClientOs,
        ReportColumn.ClientOsVersion,
        ReportColumn.ClientBrowser,
        ReportColumn.ClientBrowserVersion,
        ReportColumn.ClientLeftAt,
        ...(settings.featuresAvailability.assistantEnabled ? [ReportColumn.AssistantName] : []),
        ...(settings.featuresAvailability.guestEnabled ? [ReportColumn.GuestNames] : []),
        ReportColumn.Group,
        ReportColumn.SubGroup,
        ReportColumn.Activity,
        ReportColumn.Result,
        ReportColumn.ResultInformation,
        ReportColumn.FileName,
        ...(canSeeFilePipeline ? [ReportColumn.FileStatus] : [])
    ], [canSeeFilePipeline, settings.featuresAvailability.assistantEnabled, settings.featuresAvailability.guestEnabled]);

    const buildFileHeader = useCallback((key: string, index: number, hideIndex: boolean): string => {
        const baseHeader = t(key);
        return hideIndex ? baseHeader : `${baseHeader} (${index + 1})`;
    }, [t]);

    const buildJSON = useCallback((selectedColumns: ReportColumn[], reportData: string[][]) => {
        const fileColumnIncluded = selectedColumns.includes(ReportColumn.FileName) ||
            selectedColumns.includes(ReportColumn.FileStatus);
        return reportData.map((session) => reportColumns.reduce((acc, column, index) => {
            if (selectedColumns.includes(column)) {
                if ([ReportColumn.FileName, ReportColumn.FileStatus].includes(column)) {
                    for (let i = 0; i < externalFilesCount; i++) {
                        const key = buildFileHeader(T_KEYS.COLUMNS[column], i, externalFilesCount === 1);
                        const file = (session[index] as any)?.[i];

                        if (fileColumnIncluded) {
                            acc[key] = file;
                        }
                    }
                } else {
                    acc[t(T_KEYS.COLUMNS[column])] = session[index];
                }
            }

            return acc;
        }, {}));
    }, [t, reportColumns, externalFilesCount, buildFileHeader]);

    const buildHeaders = useCallback((selectedColumns: ReportColumn[]): string[] => {
        const nonFileHeaders = xor(selectedColumns, [ReportColumn.FileName, ReportColumn.FileStatus])
            .map((col) => t(T_KEYS.COLUMNS[col]));
        const fileHeaders = [];
        for (let i = 0; i < externalFilesCount; i++) {
            if (selectedColumns.includes(ReportColumn.FileName)) {
                fileHeaders.push(buildFileHeader(T_KEYS.COLUMNS.FILENAME, i, externalFilesCount === 1));
            }
            if (selectedColumns.includes(ReportColumn.FileStatus)) {
                fileHeaders.push(buildFileHeader(T_KEYS.COLUMNS.FILE_STATUS, i, externalFilesCount === 1));
            }
        }

        return [...nonFileHeaders, ...fileHeaders];
    }, [t, externalFilesCount, buildFileHeader]);

    const createReportFile = useCallback(async (items: string[][], modalResult: ModalResult) => {
        const {selectedColumns} = modalResult;
        const {startDate, endDate} = filters;
        const fileExtension = 'xlsx';
        const from = moment(startDate).format(fileDateFormat);
        const to = moment(endDate).format(fileDateFormat);
        const fileName = `${t(T_KEYS.FILE_NAME_PREFIX)} ${from}-${to}.${fileExtension}`;

        const xlsx = await lazyModules.xlsx();
        if (!xlsx) {
            return;
        }

        const wb = xlsx.utils.book_new();
        const sheetName = t(T_KEYS.SHEET_NAME);

        const writeOpts: WritingOptions = {
            bookType: fileExtension,
            bookSST: true,
            type: 'binary',
            sheet: sheetName
        };

        try {
            const json = buildJSON(selectedColumns, items);
            const header = buildHeaders(selectedColumns);

            const ws = xlsx.utils.json_to_sheet(json, {header});

            wb.SheetNames.push(sheetName);
            wb.Sheets[sheetName] = ws;
            wb.Props = {Author: APP_NAME};

            xlsx.writeFile(wb, fileName, writeOpts);
        } catch {
            notifier.error({title: t(T_KEYS.ERROR.FILE_GENERATE)});
        } finally {
            setDownloadModalResult(null);
        }

    }, [t, filters, fileDateFormat, buildJSON, buildHeaders]);

    const requestLabels = useMemo(() => ({
        filePipelineStep: {
            success: t(ACTIVITY_T_KEYS.FILE_PIPELINE_STEP_STATUS.SUCCESS),
            error: t(ACTIVITY_T_KEYS.FILE_PIPELINE_STEP_STATUS.ERROR),
            inProgress: t(ACTIVITY_T_KEYS.FILE_PIPELINE_STEP_STATUS.IN_PROGRESS),
            initial: t(ACTIVITY_T_KEYS.FILE_PIPELINE_STEP_STATUS.INITIAL),
        },
        sessionResult: {
            success: t(T_KEYS.SESSION_RESULT.SUCCESS),
            failed: t(T_KEYS.SESSION_RESULT.FAILED),
            reassigned: t(T_KEYS.SESSION_RESULT.REASSIGNED),
            clientLeft: t(T_KEYS.SESSION_RESULT.CLIENT_LEFT),
            agentLeft: t(T_KEYS.SESSION_RESULT.AGENT_LEFT),
            appointmentNotStarted: t(T_KEYS.SESSION_RESULT.APPOINTMENT_NOT_STARTED),
            appointmentRequested: t(T_KEYS.SESSION_RESULT.APPOINTMENT_REQUESTED),
            callingTimeout: t(T_KEYS.SESSION_RESULT.CALLING_TIMEOUT)
        },
        status: {
            agentsUnavailable: t(TRANSLATION_KEYS.ONLINE_SESSIONS_PAGE.TABLE.CLIENT_STATUS.AGENTS_UNAVAILABLE),
            afterHours: t(TRANSLATION_KEYS.ONLINE_SESSIONS_PAGE.TABLE.CLIENT_STATUS.AFTER_HOURS)
        },
        activity: {
            [SessionActivityName.AudioVideo]: t(ACTIVITY_NAMES.AUDIO_VIDEO),
            [SessionActivityName.Audio]: t(ACTIVITY_NAMES.AUDIO),
            [SessionActivityName.CoBrowsing]: t(ACTIVITY_NAMES.CO_BROWSING),
            [SessionActivityName.TextChat]: t(ACTIVITY_NAMES.TEXT_CHAT),
            [SessionActivityName.ScreenSharing]: t(ACTIVITY_NAMES.SCREEN_SHARING),
            [SessionActivityName.SIP]: t(ACTIVITY_NAMES.SIP),
            [SessionActivityName.Avatar]: t(ACTIVITY_NAMES.AVATAR),
            [SessionActivityName.AgentChatbot]: t(ACTIVITY_NAMES.AGENT_CHATBOT)
        }
    }), [t]);

    const dataRequest = useMemo(() => new DataRequest<PaginatedData<string[]>>({
        mergeResults: (acc, current) => [...(acc || []), ...current.items],
        formatResponse: (data) => ({
            status: data.page === data.pages ? TaskStatus.Completed : TaskStatus.InProgress,
            progress: Math.round(data.page / data.pages * 100),
            data
        }),
        onRequest: (data) => {
            const _filters = {
                ...filters,
                startDate: filters.startDate.format(REQUEST_SESSION_HISTORY_DATE_FORMAT),
                endDate: filters.endDate.format(REQUEST_SESSION_HISTORY_DATE_FORMAT),
            };

            return sessionHistoryApi.getSessionHistoryReport(_filters, {page: data?.page ? (data.page + 1) : 1}, requestLabels);
        },
        onProgress: setReportRequestProgress,
        onSuccess: setReportItems,
        onError: () => notifier.error({title: t(T_KEYS.ERROR.REPORT_DOWNLOAD)}),
        onComplete: () => {
            setReportLoading(false);
            setReportRequestProgress(0);
        }
    }), [filters, t, setReportItems, requestLabels]);

    const onDownloadModalClose = useCallback((result?: ModalResult) => {
        setIsDownloadModalOpen(false);
        setDownloadModalResult(result);
    }, []);

    useEffect(() => {
        if (!prevDownloadModalResult && downloadModalResult) {
            setReportLoading(true);
            setReportRequestProgress(0);
            dataRequest.execute();
        }
    }, [prevDownloadModalResult, downloadModalResult, dataRequest]);

    useEffect(() => {
        if (!reportLoading && prevReportLoading && downloadModalResult && !isEmpty(reportItems)) {
            createReportFile(reportItems, downloadModalResult);
        }
    }, [reportLoading, prevReportLoading, downloadModalResult, reportItems, createReportFile]);

    useEffect(() => () => {
        dataRequest.cancel();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return pagination.count > 0 ? (
        <div className={className}>
            {reportLoading && <GlobalLoader value={reportRequestProgress} />}

            <Button
                icon='arrow-down'
                loading={isLoading}
                disabled={disabled || reportLoading}
                onClick={() => setIsDownloadModalOpen(true)}
            >
                {t(T_KEYS.BUTTONS.DOWNLOAD_REPORT)}
            </Button>

            <ExportReportModal
                open={isDownloadModalOpen}
                reportColumns={reportColumns}
                onClose={onDownloadModalClose}
            />
        </div>
    ) : null;
};